'use client'

import { sanitizeRedirectPath } from '@/services/auth/redirectPath'
import { clearRedirectAttempted } from '@/services/auth/publicAuthRedirectMarker.client'

export const POST_AUTH_REDIRECT_KEY = 'post-auth-redirect-v1'
export const LEGACY_POST_AUTH_REDIRECT_KEY = 'redirectAfterLogin'
export const SIGNUP_CONFIRMATION_STORAGE_KEY = 'signup_confirmation'

function readStorage(): Storage | null {
  if (typeof window === 'undefined') return null

  try {
    return window.localStorage
  } catch {
    return null
  }
}

const REDIRECT_TTL_MS = 10 * 60 * 1000 // 10 minutes

export function storePostAuthRedirectPath(path: string): void {
  const storage = readStorage()
  if (!storage) return

  const safeRedirectPath = sanitizeRedirectPath(path, '/')
  if (safeRedirectPath === '/') {
    clearPostAuthRedirectPath()
    return
  }

  try {
    storage.setItem(
      POST_AUTH_REDIRECT_KEY,
      JSON.stringify({
        path: safeRedirectPath,
        expiresAt: Date.now() + REDIRECT_TTL_MS,
      }),
    )
    storage.removeItem(LEGACY_POST_AUTH_REDIRECT_KEY)
  } catch {}
}

export function readPostAuthRedirectPath(fallback: string = '/'): string {
  const storage = readStorage()
  if (!storage) return fallback

  try {
    // Try new format (JSON with TTL) first
    const raw = storage.getItem(POST_AUTH_REDIRECT_KEY)
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as { path: string; expiresAt: number }
        if (parsed.expiresAt > Date.now()) {
          return sanitizeRedirectPath(parsed.path, fallback)
        }
        // Expired — clean up
        storage.removeItem(POST_AUTH_REDIRECT_KEY)
      } catch {
        // Legacy plain-string format — still honour it
        return sanitizeRedirectPath(raw, fallback)
      }
    }

    // Fallback to legacy key
    const legacyPath = storage.getItem(LEGACY_POST_AUTH_REDIRECT_KEY)
    if (legacyPath) {
      storage.removeItem(LEGACY_POST_AUTH_REDIRECT_KEY)
      return sanitizeRedirectPath(legacyPath, fallback)
    }

    return fallback
  } catch {
    return fallback
  }
}

export function resolvePostAuthRedirectPath(
  redirectUrl: string | null | undefined,
  fallback: string = '/',
): string {
  return sanitizeRedirectPath(
    redirectUrl ?? null,
    readPostAuthRedirectPath(fallback),
  )
}

export function clearPostAuthRedirectPath(): void {
  const storage = readStorage()
  if (!storage) return

  try {
    storage.removeItem(POST_AUTH_REDIRECT_KEY)
    storage.removeItem(LEGACY_POST_AUTH_REDIRECT_KEY)
  } catch {}
}

export function clearClientAuthStateMarkers(): void {
  clearRedirectAttempted()

  const storage = readStorage()
  if (!storage) return

  clearPostAuthRedirectPath()

  try {
    storage.removeItem(SIGNUP_CONFIRMATION_STORAGE_KEY)
  } catch {}
}
